import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import Navbar from '../components/Navbar';
import { Users, Copy, Gift, TrendingUp, Award, RefreshCw } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

const ReferralPage = () => {
    const { user, updateUser } = useAuthStore();
    const [stats, setStats] = useState({
        totalReferrals: 0,
        level1Count: 0,
        level2Count: 0,
        level3Count: 0,
        totalEarnings: 0,
        pendingEarnings: 0
    });
    const [referrals, setReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState(null);

    const referralCode = user?.referralCode || 'Loading...';
    const referralLink = `${window.location.origin}/register?ref=${referralCode}`;

    useEffect(() => {
        // Fetch fresh user data to ensure referralCode is up-to-date
        const fetchUserProfile = async () => {
            try {
                const res = await api.get('/auth/profile');
                if (res.data.user?.referralCode && res.data.user.referralCode !== user?.referralCode) {
                    // Update user in store with fresh data
                    updateUser(res.data.user);
                }
            } catch (error) {
                console.error('Failed to fetch profile');
            }
        };
        fetchUserProfile();
        fetchReferralData();
        fetchSettings();
    }, []);

    const fetchReferralData = async () => {
        try {
            setLoading(true);
            const res = await api.get('/referrals/my-stats');
            if (res.data.stats) setStats(res.data.stats);
            if (res.data.referrals) setReferrals(res.data.referrals);
        } catch (error) {
            console.error('Failed to load referral data');
        } finally {
            setLoading(false);
        }
    };

    const fetchSettings = async () => {
        try {
            const res = await api.get('/referrals/settings');
            if (res.data.settings) setSettings(res.data.settings);
        } catch (error) {
            console.error('Failed to load settings');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-400 text-sm">{title}</p>
                    <p className="text-2xl font-bold text-white mt-1">{value}</p>
                    {subtext && <p className="text-gray-500 text-xs mt-1">{subtext}</p>}
                </div>
                <div className={`p-3 rounded-lg ${color}`}><Icon size={24} /></div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-900 flex flex-col">
            <Navbar />
            <main className="flex-1 container mx-auto px-4 py-8">
                <div className="max-w-5xl mx-auto">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                                <Users className="text-purple-400" /> Referral Program
                            </h1>
                            <p className="text-gray-400 text-sm">Invite friends and earn commissions</p>
                        </div>
                        <button onClick={fetchReferralData} className="p-2 text-gray-400 hover:text-white bg-slate-800 rounded-lg">
                            <RefreshCw size={18} />
                        </button>
                    </div>

                    {/* Referral Link Card */}
                    <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-xl border border-purple-500/30 p-6 mb-6">
                        <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                            <Gift className="text-purple-400" /> Your Referral Link
                        </h2>
                        <p className="text-gray-400 text-sm mb-4">Share this link with friends to earn commissions on their trades</p>

                        <div className="flex flex-col sm:flex-row gap-2">
                            <input type="text" value={referralLink} readOnly className="w-full sm:flex-1 px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white text-sm" />
                            <button onClick={() => copyToClipboard(referralLink)} className="px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-500 flex items-center gap-2">
                                <Copy size={18} /> Copy
                            </button>
                        </div>

                        <div className="mt-4 flex items-center gap-4 text-sm">
                            <div className="flex items-center gap-2">
                                <span className="text-gray-400">Your Code:</span>
                                <span className="text-purple-400 font-mono font-bold">{referralCode}</span>
                                <button onClick={() => copyToClipboard(referralCode)} className="text-gray-400 hover:text-white">
                                    <Copy size={14} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Commission Structure */}
                    {settings && (
                        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-6">
                            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                <TrendingUp className="text-green-400" /> Commission Structure
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-center">
                                    <Award className="text-green-400 mx-auto mb-2" size={32} />
                                    <p className="text-green-400 font-bold text-2xl">{settings.level1Commission || 30}%</p>
                                    <p className="text-gray-400 text-sm">Level 1</p>
                                    <p className="text-xs text-gray-500">Direct Referrals</p>
                                </div>
                                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                                    <Award className="text-blue-400 mx-auto mb-2" size={32} />
                                    <p className="text-blue-400 font-bold text-2xl">{settings.level2Commission || 20}%</p>
                                    <p className="text-gray-400 text-sm">Level 2</p>
                                    <p className="text-xs text-gray-500">2nd Level</p>
                                </div>
                                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 text-center">
                                    <Award className="text-purple-400 mx-auto mb-2" size={32} />
                                    <p className="text-purple-400 font-bold text-2xl">{settings.level3Commission || 10}%</p>
                                    <p className="text-gray-400 text-sm">Level 3</p>
                                    <p className="text-xs text-gray-500">3rd Level</p>
                                </div>
                            </div>
                            <p className="text-gray-500 text-xs text-center mt-4">
                                Commission is calculated as % of trading fee generated by your referrals
                            </p>
                        </div>
                    )}

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <StatCard title="Total Referrals" value={stats.totalReferrals} icon={Users} color="bg-purple-500/20 text-purple-400" />
                        <StatCard title="Level 1" value={stats.level1Count} icon={Award} color="bg-green-500/20 text-green-400" />
                        <StatCard title="Total Earnings" value={`₹${stats.totalEarnings.toLocaleString()}`} icon={TrendingUp} color="bg-cyan-500/20 text-cyan-400" />
                        <StatCard title="Pending" value={`₹${stats.pendingEarnings.toLocaleString()}`} icon={Gift} color="bg-yellow-500/20 text-yellow-400" />
                    </div>

                    {/* Referrals List */}
                    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                        <h2 className="text-lg font-bold text-white p-4 border-b border-slate-700">Your Referrals</h2>
                        {loading ? (
                            <div className="text-center py-12 text-gray-400">Loading...</div>
                        ) : referrals.length === 0 ? (
                            <div className="text-center py-16 text-gray-400">
                                <Users size={48} className="mx-auto mb-4 text-purple-400/50" />
                                <p className="text-lg">No referrals yet</p>
                                <p className="text-sm">Share your link to start earning</p>
                            </div>
                        ) : (
                            <table className="w-full">
                                <thead className="bg-slate-900/50">
                                    <tr className="text-left text-gray-400 text-sm">
                                        <th className="px-4 py-3">User</th>
                                        <th className="px-4 py-3">Level</th>
                                        <th className="px-4 py-3">Joined</th>
                                        <th className="px-4 py-3">Earnings</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {referrals.map((ref, i) => (
                                        <tr key={i} className="hover:bg-slate-700/30">
                                            <td className="px-4 py-3 text-white">{ref.email || `User ${i + 1}`}</td>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-1 rounded text-xs ${ref.level === 1 ? 'bg-green-500/20 text-green-400' : ref.level === 2 ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>
                                                    Level {ref.level}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-gray-400">{new Date(ref.joinedAt).toLocaleDateString()}</td>
                                            <td className="px-4 py-3 text-green-400">₹{(ref.earnings || 0).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ReferralPage;
